import {
  BrowserClient,
  defaultStackParser,
  getCurrentScope,
  makeFetchTransport,
  breadcrumbsIntegration,
  globalHandlersIntegration,
  linkedErrorsIntegration,
  dedupeIntegration,
  eventFiltersIntegration,
  extraErrorDataIntegration,
  withScope,
  captureException,
  replayIntegration,
} from "@sentry/gatsby";
import Cookies from "js-cookie";
import {
  GATSBY_SENTRY_DEV_DSN,
  GATSBY_SENTRY_ENV,
  GATSBY_SENTRY_PROD_DSN,
} from "../config";

export interface SentryType {
  tags?: Record<string, string>;
  extras?: Record<string, string>;
}

/**
 * init sentry client - using BrowserClient for tree shaking
 * https://docs.sentry.io/platforms/javascript/guides/gatsby/configuration/tree-shaking/
 */
export const initSentry = (): void => {
  if (typeof window === "undefined") return;

  const client = new BrowserClient({
    dsn:
      GATSBY_SENTRY_ENV === "production"
        ? GATSBY_SENTRY_PROD_DSN
        : GATSBY_SENTRY_DEV_DSN,
    environment: GATSBY_SENTRY_ENV,
    enabled: GATSBY_SENTRY_ENV === "production",
    replaysOnErrorSampleRate: 1.0,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    // Absolute minimum integrations for error tracking
    // https://docs.sentry.io/platforms/javascript/guides/gatsby/configuration/integrations/
    integrations: [
      globalHandlersIntegration(),
      dedupeIntegration(),
      breadcrumbsIntegration(),
      linkedErrorsIntegration(),
      eventFiltersIntegration(),
      extraErrorDataIntegration(),
      replayIntegration(),
    ],
    ignoreErrors: [
      /.*\(analytics\)$/i, // marketing analytics scripts
      /Loading\schunk\s\d+\sfailed.*/i, // chunk loading errors (slow internet)
      // Random plugins/extensions
      "top.GLOBALS",
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.epicplay.com",
      "Can't find variable: ZiteReader",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "http://loading.retry.widdit.com/",
      "atomicFindClose",
      // Facebook borked
      "fb_xd_fragment",
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      "conduitPage",
      // See https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
      "Non-Error promise rejection captured",
      "window.lintrk is not a function",
      // ZoomInfo blob URL syntax errors - third party script issue
      // https://flexport.sentry.io/issues/1628445243/events/?project=1847116
      /Unexpected token '<'/i,
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],

    // Default tags
    initialScope: {
      tags: {
        appName: "flexport-static-site",
        affected_team: "growth",
      },
    },

    // Before send hook for custom logic
    beforeSend(event) {
      // Add is_flexport_customer tag
      if (event.tags) {
        const isFlexportCustomer = (
          document.cookie.indexOf("flexport_domain=clients") > -1
        ).toString();
        event.tags.is_flexport_customer = isFlexportCustomer;
      }
      return event;
    },
  });

  // Set the client and initialize it
  getCurrentScope().setClient(client);
  client.init();
};

/**
 * helper method to log error to sentry
 * @param {Error} error
 * @param {SentryType | null | undefined} context
 *
 * @return {void}
 */
export const logErrorToSentry = (
  error: Error,
  context?: SentryType | null | undefined
): void => {
  if (typeof window !== "undefined") {
    const {tags = {}, extras = {}} = context || {};
    const isFlexportCustomer: string = (
      Cookies.get("flexport_domain") === "clients"
    ).toString();

    withScope(scope => {
      scope.setTags({
        ...tags,
        appName: "flexport-static-site",
        /* eslint-disable camelcase */
        affected_team: "growth",
        is_flexport_customer: isFlexportCustomer,
        /* eslint-enable camelcase */
      });
      scope.setExtras(extras);
      captureException(error);
    });
  }
};
